// Marketing Hub: Last-Minute-Radar · Freigaben · Plattformen · Inserate · Auswertung · Protokoll
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MetaConnectCard } from "./MetaConnect";
import { CanvaPanel } from "./CanvaPanel";
import {
  Megaphone, Sun, Cloud, CloudRain, Snowflake, CloudFog, Zap, Check, X, Loader2,
  TrendingDown, TrendingUp, Minus, ShieldCheck, Bot, Plug, ListChecks, BarChart3, ScrollText,
  Store, Bell, CalendarClock, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "radar", label: "Last-Minute-Radar", icon: Bell },
  { key: "approvals", label: "Freigaben", icon: ListChecks },
  { key: "platforms", label: "Plattformen", icon: Plug },
  { key: "listings", label: "Inserate", icon: Store },
  { key: "analytics", label: "Auswertung", icon: BarChart3 },
  { key: "audit", label: "Protokoll", icon: ScrollText },
];

const URG = {
  critical: { label: "JETZT HANDELN", color: "#F43F5E" },
  warning: { label: "BALD", color: "#F59E0B" },
  info: { label: "CHANCE", color: "#38BDF8" },
  low: { label: "RUHIG", color: "#9CA3AF" },
};

export function MarketingHub() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("meta") ? "platforms" : "radar");
  const [settings, setSettings] = useState(null);
  const [pending, setPending] = useState(0);
  const [msg, setMsg] = useState("");

  const refreshPending = useCallback(async () => {
    const r = await axios.get(`${API}/marketing/approvals?status=pending`);
    setPending(r.data.length);
  }, []);

  useEffect(() => {
    axios.get(`${API}/marketing/settings`).then((r) => setSettings(r.data));
    refreshPending();
  }, [refreshPending, tab]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3500); };

  const toggleAutopilot = async () => {
    const next = settings.global_mode === "auto" ? "approval" : "auto";
    await axios.put(`${API}/marketing/settings`, { ...settings, global_mode: next });
    setSettings({ ...settings, global_mode: next });
    flash(next === "auto" ? "Autopilot aktiv – Plattformen im Modus „Auto“ veröffentlichen ohne Rückfrage" : "Freigabe-Modus aktiv – jede Aktion wird vorher abgefragt");
  };

  return (
    <div className="space-y-5" data-testid="marketing-hub">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
            <Megaphone size={22} className="text-eb-primary" /> Marketing-Hub
          </h2>
          <p className="text-eb-muted text-sm">Social Media · Marktplätze · Newsletter – gesteuert vom Marketing-Manager-Agenten, verknüpft mit Preis, Markt & Wetter</p>
        </div>
        <div className="flex items-center gap-3">
          {msg && <span className="text-emerald-400 text-xs max-w-xs" data-testid="marketing-msg">{msg}</span>}
          {settings && (
            <button onClick={toggleAutopilot} data-testid="autopilot-toggle"
              className={`btn ${settings.global_mode === "auto" ? "btn-primary" : ""}`}>
              {settings.global_mode === "auto" ? <Bot size={14} /> : <ShieldCheck size={14} />}
              {settings.global_mode === "auto" ? "Autopilot AN" : "Freigabe-Modus"}
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1 flex-wrap eb-surface rounded-lg p-1 w-fit" data-testid="marketing-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`marketing-tab-${key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${tab === key ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted hover:text-white"}`}>
            <Icon size={13} /> {label}
            {key === "approvals" && pending > 0 && (
              <span className="ml-1 px-1.5 rounded-full bg-rose-500 text-white text-[10px]" data-testid="pending-count">{pending}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "radar" && <LastMinuteRadar onQueued={(m) => { flash(m); refreshPending(); }} />}
      {tab === "approvals" && <ApprovalQueue onChange={refreshPending} flash={flash} />}
      {tab === "platforms" && <PlatformGrid flash={flash} />}
      {tab === "listings" && <ListingsView onQueued={(m) => { flash(m); refreshPending(); }} />}
      {tab === "analytics" && <MarketingAnalytics />}
      {tab === "audit" && <AuditLog />}
    </div>
  );
}

/* ---------------- Last-Minute-Radar ---------------- */
function WeatherIcon({ d }) {
  const s = 14;
  if (d.snow_cm >= 1) return <Snowflake size={s} className="text-sky-300" />;
  if (d.rain_mm >= 2) return <CloudRain size={s} className="text-sky-400" />;
  if (d.code === 45 || d.code === 48) return <CloudFog size={s} className="text-gray-400" />;
  if (d.code <= 1) return <Sun size={s} className="text-amber-400" />;
  return <Cloud size={s} className="text-gray-300" />;
}

function TrendBadge({ m }) {
  const Icon = m.direction === "fallend" ? TrendingDown : m.direction === "steigend" ? TrendingUp : Minus;
  const col = m.direction === "fallend" ? "text-rose-400" : m.direction === "steigend" ? "text-emerald-400" : "text-eb-muted";
  return <span className={`flex items-center gap-1 ${col}`}><Icon size={12} /> Markt {m.direction} {m.change_pct > 0 ? "+" : ""}{m.change_pct} %</span>;
}

function OpportunityCard({ o, onLaunch, launching }) {
  const [disc, setDisc] = useState(o.offer.discount_pct);
  const u = URG[o.urgency];
  const price = Math.round(o.base_price * (1 - disc / 100));
  const freeSet = new Set(o.free_dates);
  return (
    <div className="card" style={{ borderLeft: `3px solid ${u.color}` }} data-testid={`opportunity-${o.property_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="channel-pill" style={{ background: u.color + "22", color: u.color }}>{u.label}</span>
            <span className="font-mono text-[11px] text-eb-muted">Score {o.score}</span>
          </div>
          <div className="font-heading font-bold text-lg mt-1">{o.name}</div>
          <div className="text-xs text-eb-muted">{o.location} · {o.season.label}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-eb-muted line-through font-mono">€ {o.base_price}</div>
          <div className="font-heading font-bold text-2xl text-eb-primary" data-testid={`offer-price-${o.property_id}`}>€ {price}</div>
          <div className="text-[11px] text-eb-muted">pro Nacht · −{disc} %</div>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mt-4" data-testid={`weather-strip-${o.property_id}`}>
        {o.weather.map((d) => (
          <div key={d.date} title={`${d.label} · ${d.rain_mm} mm`}
            className={`rounded p-1.5 text-center border ${freeSet.has(d.date) ? "border-eb-primary/50" : "border-white/5 opacity-50"}`}>
            <div className="text-[9px] font-mono text-eb-muted">{d.date.slice(8)}.{d.date.slice(5, 7)}</div>
            <div className="flex justify-center my-0.5"><WeatherIcon d={d} /></div>
            <div className="text-[10px] font-semibold">{Math.round(d.t_max)}°</div>
            <div className={`text-[9px] ${freeSet.has(d.date) ? "text-eb-primary" : "text-eb-muted"}`}>{freeSet.has(d.date) ? "frei" : "belegt"}</div>
          </div>
        ))}
      </div>
      {o.weather_error && <div className="text-[11px] text-amber-400 mt-1">{o.weather_error}</div>}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mt-3">
        <span className="flex items-center gap-1"><CalendarClock size={12} className="text-eb-primary" /> {o.free_next7} freie Nächte (7 T.)</span>
        <span className="flex items-center gap-1"><Sun size={12} className="text-amber-400" /> {o.good_weather_free_days.length} Schönwetter-Tage frei</span>
        <TrendBadge m={o.market} />
      </div>
      {o.reasons.length > 0 && <div className="text-xs text-eb-muted mt-2">{o.reasons.join(" · ")}</div>}

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <div className="overline mb-1">Rabatt {disc} %</div>
          <input type="range" min="0" max="35" step="1" value={disc} style={{ padding: 0 }}
            onChange={(e) => setDisc(parseInt(e.target.value))} data-testid={`discount-slider-${o.property_id}`} />
        </div>
        <button className="btn btn-primary" disabled={launching} onClick={() => onLaunch(o, disc)}
          data-testid={`launch-campaign-${o.property_id}`}>
          {launching ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Komplett-Kampagne vorbereiten
        </button>
      </div>
    </div>
  );
}

function LastMinuteRadar({ onQueued }) {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [launching, setLaunching] = useState(null);

  const load = async () => {
    const [o, a] = await Promise.all([
      axios.get(`${API}/marketing/lastminute/opportunities`),
      axios.get(`${API}/marketing/alerts`),
    ]);
    setData(o.data);
    setAlerts(a.data.alerts.filter((x) => !x.lastminute));
  };
  useEffect(() => { load(); }, []);

  const launch = async (o, disc) => {
    setLaunching(o.property_id);
    try {
      const r = await axios.post(`${API}/marketing/lastminute/campaign`, { property_id: o.property_id, discount_pct: disc });
      onQueued(`✓ ${r.data.queued.length} Aktionen für „${r.data.property}“ vorbereitet – bitte unter „Freigaben“ prüfen`);
    } catch (e) {
      onQueued(`Fehler: ${e.response?.data?.detail || e.message}`);
    } finally { setLaunching(null); }
  };

  const ack = async (id) => { await axios.post(`${API}/marketing/alerts/${id}/ack`); load(); };

  if (!data) return <div className="text-eb-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Analysiere Belegung, Markt & Wetter…</div>;
  return (
    <div className="space-y-4" data-testid="lastminute-radar">
      <div className="card flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">Saison-Steuerung</div>
          <div className="text-sm">{data.season.label} · Gutes Wetter = <span className="text-eb-primary">{data.season.good_weather}</span></div>
        </div>
        <div className="text-[11px] text-eb-muted">Wetter live via Open-Meteo · Markt aus Wettbewerbs-Scan · Belegung aus easybooking</div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {data.opportunities.map((o) => (
          <OpportunityCard key={o.property_id} o={o} onLaunch={launch} launching={launching === o.property_id} />
        ))}
      </div>
      {alerts.length > 0 && (
        <div className="card">
          <div className="overline mb-3">Weitere Werbe-Signale</div>
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className={`flex items-start justify-between gap-3 p-3 rounded-md eb-surface ${a.acknowledged ? "opacity-50" : ""}`}
                data-testid={`alert-${a.id}`}>
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: URG[a.level].color }} /> {a.title}
                  </div>
                  <div className="text-xs text-eb-muted">{a.reason}</div>
                  <div className="text-xs mt-1"><Sparkles size={11} className="inline text-eb-primary" /> {a.suggestion}</div>
                </div>
                {!a.acknowledged && (
                  <button className="btn !py-1 !px-2 text-xs" onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}>
                    <Check size={12} /> Erledigt
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Approval Queue ---------------- */
const KIND_LABEL = { post: "Beitrag", ad: "Anzeige", listing: "Inserat", newsletter: "Newsletter", rate: "Preis" };

function ApprovalQueue({ onChange, flash }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => { const r = await axios.get(`${API}/marketing/approvals`); setItems(r.data); onChange(); };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const act = async (id, what) => {
    setBusy(id);
    try { await axios.post(`${API}/marketing/approvals/${id}/${what}`, {}); await load(); }
    finally { setBusy(null); }
  };
  const approveAll = async () => {
    setBusy("all");
    const r = await axios.post(`${API}/marketing/approvals/approve-all`);
    flash(`✓ ${r.data.executed} Aktionen freigegeben & ausgeführt`);
    await load(); setBusy(null);
  };
  const pending = items.filter((i) => i.status === "pending");
  const done = items.filter((i) => i.status !== "pending").slice(0, 30);

  return (
    <div className="space-y-4" data-testid="approval-queue">
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Offene Vorschläge des Manager-Agenten ({pending.length})</div>
          {pending.length > 0 && (
            <button className="btn btn-primary" onClick={approveAll} disabled={busy === "all"} data-testid="approve-all-btn">
              {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Alle freigeben
            </button>
          )}
        </div>
        {pending.length === 0 && <div className="text-sm text-eb-muted" data-testid="no-pending">Keine offenen Aktionen – alles erledigt.</div>}
        <div className="space-y-2">
          {pending.map((i) => (
            <div key={i.id} className="p-3 rounded-md eb-surface flex items-start justify-between gap-3" data-testid={`approval-${i.id}`}>
              <div className="flex gap-3">
                <Bot size={18} className="text-eb-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-[11px] font-mono text-eb-muted uppercase">{KIND_LABEL[i.kind]} · {i.platform}</div>
                  <div className="text-sm">{i.protocol}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button className="btn !py-1 !px-2 text-xs !border-emerald-500 text-emerald-400" disabled={busy === i.id}
                  onClick={() => act(i.id, "approve")} data-testid={`approve-${i.id}`}><Check size={12} /> OK</button>
                <button className="btn btn-danger !py-1 !px-2 text-xs" disabled={busy === i.id}
                  onClick={() => act(i.id, "reject")} data-testid={`reject-${i.id}`}><X size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="overline mb-3">Verlauf</div>
        <div className="space-y-1">
          {done.map((i) => (
            <div key={i.id} className="flex items-center justify-between text-xs py-1.5 border-b border-white/5">
              <span className="truncate">{KIND_LABEL[i.kind]} · {i.title} · <span className="text-eb-muted">{i.platform}</span></span>
              <span className={i.status === "executed" ? "text-emerald-400" : "text-rose-400"}>
                {i.status === "executed" ? (i.mode === "auto" ? "automatisch ausgeführt" : "freigegeben") : "abgelehnt"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Platforms ---------------- */
function PlatformCard({ p, onSave }) {
  const [token, setToken] = useState("");
  return (
    <div className="card !p-4" data-testid={`platform-${p.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-sm">{p.label}</div>
          <div className="text-[10px] font-mono text-eb-muted">{p.api} · {p.region}</div>
        </div>
        <span className="channel-pill" style={{ background: p.live ? "#10B98122" : p.connected ? "#F59E0B22" : "#9CA3AF22", color: p.live ? "#10B981" : p.connected ? "#F59E0B" : "#9CA3AF" }} data-testid={`platform-state-${p.key}`}>
          {p.live ? "live" : p.connected ? "simuliert" : "aus"}
        </span>
      </div>
      <div className="flex gap-1 mt-3 eb-surface rounded-full p-1 w-fit">
        {["approval", "auto"].map((m) => (
          <button key={m} onClick={() => onSave(p.key, { mode: m })} data-testid={`platform-mode-${p.key}-${m}`}
            className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${p.mode === m ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}>
            {m === "approval" ? "Freigabe" : "Auto"}
          </button>
        ))}
      </div>
      <div className="flex gap-1 mt-3">
        <input placeholder={p.has_credentials ? "•••• gespeichert" : "API-Token / Zugang"} value={token}
          onChange={(e) => setToken(e.target.value)} className="!py-1 !text-xs" data-testid={`platform-token-${p.key}`} />
        <button className="btn !py-1 !px-2 text-xs" data-testid={`platform-connect-${p.key}`}
          onClick={() => { onSave(p.key, { connected: !p.connected, ...(token ? { credentials: { token } } : {}) }); setToken(""); }}>
          {p.connected ? "Trennen" : "Verbinden"}
        </button>
      </div>
    </div>
  );
}

function PlatformGrid({ flash }) {
  const [list, setList] = useState([]);
  const load = async () => setList((await axios.get(`${API}/marketing/platforms`)).data);
  useEffect(() => { load(); }, []);
  const save = async (key, patch) => { await axios.put(`${API}/marketing/platforms/${key}`, patch); flash("✓ Plattform gespeichert"); load(); };
  const section = (cat, title) => (
    <div>
      <div className="overline mb-2">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {list.filter((p) => p.cat === cat).map((p) => <PlatformCard key={p.key} p={p} onSave={save} />)}
      </div>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="platform-grid">
      <div className="card text-xs text-amber-300 border-amber-500/30">
        Plattformen mit „live“ veröffentlichen echt (Facebook/Instagram nach Meta-Verbindung, Brevo). Alle übrigen Adapter laufen im SIMULATIONSMODUS, bis echte API-Zugänge (TikTok for Business, Google, eBay Developer, Etsy …) hinterlegt sind. „Auto“ wirkt nur, wenn der globale Autopilot aktiv ist.
      </div>
      <MetaConnectCard flash={flash} onChanged={load} />
      <CanvaPanel flash={flash} />
      {section("social", "Social Media & Messenger")}
      {section("marketplace", "Marktplätze (Europa)")}
      {section("email", "E-Mail-Marketing")}
    </div>
  );
}

/* ---------------- Listings ---------------- */
function ListingsView({ onQueued }) {
  const [listings, setListings] = useState([]);
  const [props, setProps] = useState([]);
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => setListings((await axios.get(`${API}/marketing/listings`)).data);
  useEffect(() => {
    load();
    axios.get(`${API}/properties`).then((r) => { setProps(r.data); setPid(r.data[0]?.id || ""); });
  }, []);
  const gen = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/marketing/listings/generate`, {
        property_id: pid,
        marketplaces: ["facebook_marketplace", "google_hotel_ads", "google_merchant", "ebay", "etsy", "kleinanzeigen", "willhaben", "marktplaats", "leboncoin", "subito", "wallapop", "allegro"],
      });
      onQueued(`✓ ${r.data.listings.length} Marktplatz-Inserate zur Freigabe vorbereitet`);
      load();
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="listings-view">
      <div className="card flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="overline mb-1">Objekt auf allen EU-Marktplätzen inserieren</div>
          <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="listing-property-select">
            {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={gen} disabled={busy || !pid} data-testid="generate-listings-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Store size={14} />} Inserate erzeugen
        </button>
      </div>
      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="eb-surface">
            <th className="text-left px-3 py-2 overline">Marktplatz</th><th className="text-left px-3 py-2 overline">Titel</th>
            <th className="text-right px-3 py-2 overline">€/Nacht</th><th className="text-right px-3 py-2 overline">Status</th>
          </tr></thead>
          <tbody>
            {listings.map((l) => (
              <tr key={l.id} className="border-t border-white/5" data-testid={`listing-row-${l.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{l.marketplace}</td>
                <td className="px-3 py-2 text-xs">{l.title}</td>
                <td className="px-3 py-2 text-right font-mono">€ {Math.round(l.price_eur)}</td>
                <td className={`px-3 py-2 text-right text-xs ${l.status === "live" ? "text-emerald-400" : "text-amber-400"}`}>
                  {l.status === "live" ? `live · ${l.external_id}` : "wartet auf Freigabe"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Analytics ---------------- */
function MarketingAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/marketing/analytics`).then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-eb-muted">Lade…</div>;
  const t = d.totals;
  const kpis = [
    ["Impressionen", t.impressions.toLocaleString("de-DE")], ["Klicks", t.clicks.toLocaleString("de-DE")],
    ["Buchungen", t.bookings], ["Werbebudget", `€ ${t.spend_eur.toFixed(0)}`], ["Umsatz", `€ ${t.revenue_eur.toLocaleString("de-DE")}`],
  ];
  const maxH = Math.max(...d.best_times.matrix.flat());
  return (
    <div className="space-y-4" data-testid="marketing-analytics">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map(([k, v]) => (
          <div key={k} className="card !p-4" data-testid={`mkt-kpi-${k}`}>
            <div className="overline">{k}</div><div className="font-heading font-bold text-xl mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <div className="card !p-0 overflow-hidden">
          <div className="overline px-4 py-3 border-b border-white/5">Leistung pro Plattform</div>
          {d.platforms.length === 0 && <div className="p-4 text-sm text-eb-muted">Noch keine Veröffentlichungen – geben Sie Aktionen unter „Freigaben“ frei.</div>}
          <table className="w-full text-sm">
            <tbody>
              {d.platforms.map((p) => (
                <tr key={p.platform} className="border-t border-white/5" data-testid={`mkt-row-${p.platform}`}>
                  <td className="px-3 py-2 font-semibold">{p.label}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{p.impressions.toLocaleString("de-DE")} Impr.</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">CTR {p.ctr_pct} %</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{p.bookings} Buch.</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-eb-primary">{p.roas ? `ROAS ${p.roas}×` : "organisch"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card">
          <div className="overline mb-3">Beste Posting-Zeiten</div>
          <div className="grid gap-1" style={{ gridTemplateColumns: `28px repeat(${d.best_times.hours.length}, 1fr)` }}>
            <div />
            {d.best_times.hours.map((h) => <div key={h} className="text-[9px] text-center text-eb-muted font-mono">{h}h</div>)}
            {d.best_times.days.map((day, i) => (
              <React.Fragment key={day}>
                <div className="text-[10px] text-eb-muted">{day}</div>
                {d.best_times.matrix[i].map((v, j) => (
                  <div key={j} className="h-6 rounded-sm" title={`${day} ${d.best_times.hours[j]}h`}
                    style={{ background: `rgba(212,175,55,${(v / maxH).toFixed(2)})` }} />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Audit ---------------- */
function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API}/marketing/audit?limit=150`).then((r) => setRows(r.data)); }, []);
  return (
    <div className="card" data-testid="audit-log">
      <div className="overline mb-3">Vollständiges Aktions-Protokoll</div>
      <div className="space-y-1 font-mono text-[11px]">
        {rows.map((r) => (
          <div key={r.id} className="flex gap-3 py-1 border-b border-white/5">
            <span className="text-eb-muted shrink-0">{r.at.slice(0, 19).replace("T", " ")}</span>
            <span className="text-eb-primary shrink-0 w-24">{r.actor}</span>
            <span className="shrink-0 w-24">{r.action}</span>
            <span className="text-eb-muted">{r.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
